import React from 'react';
import {
  Typography,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../contexts/ApiContext';
import { useWithTokenRefresh } from '../utils/api';
import { toastError } from '../utils/toast';
import { BridgeTypes, NodeRegistrationTypes } from '@flashbacktech/flashbackclient/api';
import OrganizationUpgradePrompt from '../components/OrganizationUpgradePrompt';

interface OrgKeyItem {
  id: string;
  orgId: string;
  publicKey: string;
  createdAt?: string;
  nodeKeys?: Array<{ keyId: string; nodeId: string }>;
}

const PrivateBridgeNodes: React.FC = () => {
  const { authState, globalData } = useAuth();
  const { apiClient } = useApi();
  const withTokenRefresh = useWithTokenRefresh();
  const [orgId, setOrgId] = React.useState<string>('');
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);
  const [nodes, setNodes] = React.useState<BridgeTypes.NodeInfo[]>([]);
  const [totalNodes, setTotalNodes] = React.useState<number>(0);
  const [keys, setKeys] = React.useState<NodeRegistrationTypes.OrganizationKey[]>([]);
  const [loadingKeys, setLoadingKeys] = React.useState<boolean>(false);
  const [generatingKey, setGeneratingKey] = React.useState<boolean>(false);
  const [showCreateModal, setShowCreateModal] = React.useState<boolean>(false);
  const [deletingKeyId, setDeletingKeyId] = React.useState<string | null>(null);

  // Helpers
  const formatDateTime = (iso?: string) => {
    if (!iso) return '-';
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  const handleCopyText = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text || '');
    } catch (e) {
      try {
        const ta = document.createElement('textarea');
        ta.value = text || '';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      } catch (err) {
        alert('Failed to copy');
      }
    }
  };

  const handleCopyPublicKey = async (publicKey: string) => {
    try {
      await navigator.clipboard.writeText(publicKey || '');
    } catch (e) {
      try {
        const ta = document.createElement('textarea');
        ta.value = publicKey || '';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      } catch (err) {
        alert('Failed to copy public key');
      }
    }
  };

  // Use globalData.userProfile.orgId instead of API call
  React.useEffect(() => {
    if (globalData?.userProfile?.orgId) {
      setOrgId(globalData.userProfile.orgId);
    } else {
      setOrgId('');
    }
  }, [globalData?.userProfile?.orgId]);

  const loadNodes = React.useCallback(async () => {
    if (!orgId) return;
    try {
      setLoading(true);
      setError(null);
      const response = await withTokenRefresh(() => apiClient.getPrivateNodeInfo(orgId));

      if (!response.success) {
        toastError( 'Failed to retrieve organization nodes');
        return;
      }
      setNodes(response.data);
      setTotalNodes(response.total);
    } catch (error) {
      toastError('Failed to retrieve organization nodes');
    } finally {
      setLoading(false);
    }
  }, [orgId, withTokenRefresh, apiClient, toastError]);

  const loadKeys = React.useCallback(async () => {
    if (!orgId) return;
    try {
      setLoadingKeys(true);
      const response = await withTokenRefresh(() => apiClient.getOrgKeys(orgId));
      if (!response.success) {
        toastError('Failed to fetch keys');
        return;
      }
      setKeys(response.data || []);
    } catch (e: any) {
      setKeys([]);
    } finally {
      setLoadingKeys(false);
    }
  }, [orgId, withTokenRefresh, apiClient]);

  const handleGenerateOrgKey = async () => {
    const idOrg = orgId;
    if (!idOrg) {
      alert('Organization ID is required to generate a key.');
      return;
    }
    if (!authState?.token) {
      alert('You must be authenticated to generate a key.');
      return;
    }
    try {
      setGeneratingKey(true);
      // generateOrgKey returns the PEM text directly as a string
      const pemText = await withTokenRefresh(() => apiClient.generateOrgKey(idOrg));
      
      // Download PEM file immediately
      try {
        const blob = new Blob([pemText], { type: 'application/x-pem-file' });
        const dlUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = dlUrl;
        a.download = 'private_key.pem';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(dlUrl);
      } catch {
        /* ignore */
      }
      // Optionally copy to clipboard
      try {
        await handleCopyText(pemText);
      } catch {
        /* ignore */
      }
      await loadKeys();
    } catch (e: any) {
      alert(e?.message || 'Failed to generate organization key');
    } finally {
      setGeneratingKey(false);
    }
  };

  const handleDeleteKey = async (keyId: string) => {
    if (!orgId || !keyId) return;
    if (!authState?.token) return;
    try {
      const json = await withTokenRefresh(() => apiClient.deleteOrgKey(orgId, keyId));
      if (!json.success) {
        throw new Error(json?.message || 'Failed to delete key');
      }
      await loadKeys();
    } catch (e: any) {
      alert(e?.message || 'Failed to delete key');
    } finally {
      setDeletingKeyId(null);
    }
  };

  React.useEffect(() => {
    if (orgId) {
      loadNodes();
      loadKeys();
    }
  }, [orgId, loadNodes, loadKeys]);

  // Show upgrade prompt for individual accounts
  if (!globalData?.hasOrgAccess) {
    return (
      <div className="h-full flex flex-col" style={{ backgroundColor: '#FFFFFF' }}>
        <OrganizationUpgradePrompt
          title="Private Bridge Nodes"
          description="Private Bridge Nodes allow organizations to deploy dedicated infrastructure for secure data transfer. This feature requires an organization account."
          featureName="Private Bridge Nodes"
        />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: '#FFFFFF' }}>
      {/* Header */}
      <div style={{ padding: '24px 40px 24px 40px' }}>
        <Typography
          style={{
            fontSize: '32px',
            fontWeight: 700,
            color: '#1F2937',
            marginBottom: '8px',
            fontFamily: 'Inter',
            letterSpacing: '-0.02em',
          }}
        >
          Network
        </Typography>
        <Typography
          style={{
            fontSize: '16px',
            fontWeight: 400,
            color: '#6B7280',
            fontFamily: 'Inter',
            lineHeight: '24px',
          }}
        >
          Monitor and manage your bridge nodes infrastructure
        </Typography>
      </div>

      {/* Content */}
      <div className="flex-1 px-10 pb-6 overflow-y-auto">

      {/* Private Bridge Nodes Sub-section Header with buttons */}
      <Box sx={{ ml: '8px' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography sx={{ fontWeight: 600, fontSize: '18px', color: '#101828' }}>
                Private Bridge Nodes
              </Typography>
              {!loading && totalNodes > 0 && (
                <span
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                  style={{ backgroundColor: '#EEF2FF', color: '#4F46E5' }}
                >
                  {totalNodes} node{totalNodes !== 1 ? 's' : ''}
                </span>
              )}
            </Box>
            <Typography sx={{ fontSize: '14px', color: '#667085', mt: 0.5 }}>
              View and monitor your organization's private bridge nodes
            </Typography>
            {orgId && (
              <div
                onClick={() => handleCopyText(orgId)}
                className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                title="Click to copy Organization ID"
              >
                <span className="text-xs font-medium text-gray-500">Org ID:</span>
                <span className="text-xs font-mono text-gray-700">{orgId}</span>
                <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {/* Reload */}
            <Tooltip title="Reload">
              <IconButton
                size="small"
                onClick={() => { loadNodes(); loadKeys(); }}
                sx={{ width: 36, height: 36, border: '1px solid #D0D5DD', borderRadius: '8px', color: '#667085', '&:hover': { bgcolor: '#F9FAFB', borderColor: '#98A2B3' } }}
              >
                <RefreshIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            {/* Add */}
            <Tooltip title="Create private key">
              <IconButton
                size="small"
                onClick={() => setShowCreateModal(true)}
                disabled={generatingKey || !orgId}
                sx={{ width: 36, height: 36, bgcolor: '#617BFF', borderRadius: '8px', color: 'white', '&:hover': { bgcolor: '#4A67E3' }, '&:disabled': { bgcolor: '#D0D5DD' } }}
              >
                <AddIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      <div className="border-b border-gray-200 mb-6"></div>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="200px">
          <CircularProgress />
        </Box>
      ) : error ? (
        <div className="bg-white rounded-lg shadow-sm border border-red-200 p-4 text-sm text-red-600">
          {error}
        </div>
      ) : nodes.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <div className="text-gray-400 mb-4">
            <svg
              className="mx-auto h-12 w-12"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </div>
          <Typography variant="h6" className="text-gray-600 mb-2">
            No private bridge nodes found
          </Typography>
          <Typography variant="body2" className="text-gray-500">
            Your organization doesn't have any private bridge nodes yet
          </Typography>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden border border-[#E9EAEB]">
          <table className="w-full table-auto">
            <thead>
              <tr
                className="border-b border-gray-200"
                style={{ backgroundColor: 'white', height: '44px' }}
              >
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  IP
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  URL
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Region
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Version
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Updated
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {nodes.map((node) => (
                <tr
                  key={node.ip}
                  className="hover:bg-gray-50 transition-all"
                  style={{ height: '72px' }}
                >
                  <td className="px-4 py-4 truncate">
                    <Typography variant="body1" style={{ color: '#717680' }}>
                      {node.ip || '-'}
                    </Typography>
                  </td>
                  <td className="px-4 py-4 truncate">
                    <a
                      href={node.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-indigo-600 hover:underline"
                      style={{ color: '#4F46E5' }}
                    >
                      {node.url || '-'}
                    </a>
                  </td>
                  <td className="px-4 py-4 truncate">
                    <Typography variant="body1" style={{ color: '#717680' }}>
                      {node.region || '-'}
                    </Typography>
                  </td>
                  <td className="px-4 py-4 truncate">
                    <Chip
                      label={node.version || '-'}
                      size="small"
                      variant="outlined"
                      sx={{ fontWeight: 500, borderColor: '#E5E7EB', color: '#374151' }}
                    />
                  </td>
                  <td className="px-4 py-4 truncate">
                    <Chip
                      label={node.status || '-'}
                      size="small"
                      sx={{ fontWeight: 500, backgroundColor: '#F3F4F6', color: '#374151' }}
                    />
                  </td>
                  <td className="px-4 py-4 truncate">
                    <Typography variant="body1" style={{ color: '#717680' }}>
                      {formatDateTime(node.lastUpdated)}
                    </Typography>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Organization Keys Section */}
      <div className="mt-12">
        <Typography
          variant="h6"
          className="text-black mb-4"
          style={{ fontWeight: 600, fontSize: '18px', lineHeight: '28px' }}
        >
          Organization Keys
        </Typography>
        {loadingKeys ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="150px">
            <CircularProgress size={32} />
          </Box>
        ) : (
          <div className="bg-white rounded-lg shadow-md overflow-hidden border border-[#E9EAEB]">
            <table className="w-full table-auto">
              <thead>
                <tr
                  className="border-b border-gray-200"
                  style={{ backgroundColor: 'white', height: '44px' }}
                >
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Key ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Associated Nodes
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Node IDs
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Public Key
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {keys.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-4 text-center">
                      <div className="py-6 text-sm text-gray-600">
                        No keys found for this organization.
                      </div>
                    </td>
                  </tr>
                ) : (
                  keys.map((k) => {
                    const nodesCount = Array.isArray(k.nodeKeys) ? k.nodeKeys.length : 0;
                    const nodeIds = (k.nodeKeys || []).map((n: any) => n.nodeId).join(', ');
                    const pk: string = k.publicKey || '';
                    return (
                      <tr
                        key={k.id}
                        className="hover:bg-gray-50 transition-all"
                        style={{ height: '72px' }}
                      >
                        <td className="px-4 py-4 truncate">
                          <button
                            className="text-indigo-600 hover:underline font-medium"
                            style={{ color: '#4F46E5' }}
                            title={`Copy Key ID: ${k.id}`}
                            onClick={() => handleCopyText(k.id)}
                          >
                            {k.id && k.id.length > 20
                              ? `${k.id.slice(0, 12)}…${k.id.slice(-6)}`
                              : k.id || '-'}
                          </button>
                        </td>
                        <td className="px-4 py-4 truncate">
                          <Typography variant="body1" style={{ color: '#717680' }}>
                            {k.createdAt.toLocaleString()}
                          </Typography>
                        </td>
                        <td className="px-4 py-4 truncate">
                          <Chip
                            label={nodesCount}
                            size="small"
                            sx={{ fontWeight: 600, backgroundColor: '#F3F4F6', color: '#374151' }}
                          />
                        </td>
                        <td className="px-4 py-4 truncate">
                          <div
                            className="max-w-[260px] truncate"
                            style={{ color: '#717680' }}
                            title={nodeIds}
                          >
                            {nodeIds || '-'}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <button
                            onClick={() => handleCopyPublicKey(pk)}
                            className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors p-2 rounded"
                            title="Copy public key"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                              className="w-5 h-5"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75"
                              />
                            </svg>
                          </button>
                        </td>
                        <td className="px-4 py-4 text-right">
                          <Tooltip title="Delete key">
                            <IconButton
                              size="small"
                              onClick={() => setDeletingKeyId(k.id)}
                              sx={{ 
                                width: 32, 
                                height: 32, 
                                border: '1px solid #FDECEC', 
                                borderRadius: '8px', 
                                color: '#B42318', 
                                bgcolor: '#FEF3F2',
                                '&:hover': { bgcolor: '#FEE4E2' } 
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </div>

      {/* Create key confirmation modal */}
      <Dialog
        open={showCreateModal}
        onClose={() => !generatingKey && setShowCreateModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#101828' }}>
          Create organization private key
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 1 }}>
          <Typography sx={{ color: '#475467', fontSize: 14, mb: 1 }}>
            A new RSA private key will be generated for this organization. The PEM will be
            downloaded and copied to your clipboard. Keep it secure.
          </Typography>
          <ul
            style={{
              margin: 0,
              paddingLeft: 18,
              color: '#475467',
              fontSize: 13,
              lineHeight: '20px',
            }}
          >
            <li>Only public keys are stored server-side; private keys are not retained</li>
            <li>Associated nodes must use the corresponding public key</li>
            <li>Operations are logged for audit</li>
          </ul>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setShowCreateModal(false)}
            disabled={generatingKey}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={async () => {
              await handleGenerateOrgKey();
              setShowCreateModal(false);
            }}
            disabled={generatingKey}
            sx={{
              height: 32,
              px: 1.5,
              textTransform: 'none',
              fontSize: '12px',
              fontWeight: 700,
              bgcolor: '#617BFF',
              borderRadius: '8px',
              boxShadow: '0 1px 2px rgba(16,24,40,0.06)',
              '&:hover': { bgcolor: '#5A6FE8' },
            }}
          >
            {generatingKey ? 'Generating…' : 'Create key'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete key confirmation modal */}
      <Dialog open={!!deletingKeyId} onClose={() => setDeletingKeyId(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#101828' }}>
          Delete organization key
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 1 }}>
          <Typography sx={{ color: '#475467', fontSize: 14 }}>
            This action is irreversible and will remove the key and its node associations. Nodes
            using this key will need to re-register with a different key.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeletingKeyId(null)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deletingKeyId && handleDeleteKey(deletingKeyId)}
            sx={{ textTransform: 'none' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PrivateBridgeNodes;
